import { useState, useRef } from 'react';
import { API, formatDateTime } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Shield, Download, Upload, FileText, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

export function BackupCard({ backupStatus, onRefresh }) {
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [importPassword, setImportPassword] = useState('');
  const [importMode, setImportMode] = useState('merge');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const fileRef = useRef(null);

  const handleExport = async () => {
    if (!password || password.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setExporting(true);
    try {
      const res = await API.post('/backup/export', { password }, { responseType: 'blob' });
      const filename = res.headers['content-disposition']?.match(/filename="(.+)"/)?.[1] || 'backup.dat';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Backup exported successfully');
      setShowExport(false); setPassword(''); setConfirmPassword('');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally { setExporting(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setImportFile(file);
  };

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleDryRun = async () => {
    if (!importFile) { toast.error('Select a backup file'); return; }
    if (!importPassword) { toast.error('Enter password'); return; }
    setImporting(true);
    try {
      const fileData = await readFileAsBase64(importFile);
      const res = await API.post('/backup/import', {
        file_data: fileData, password: importPassword, mode: importMode, dry_run: true,
      });
      setDryRunResult(res.data.summary);
      toast.success('Dry run complete - review summary below');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed');
    } finally { setImporting(false); }
  };

  const handleImport = async () => {
    if (!importFile || !importPassword) return;
    if (!window.confirm(`Apply import in "${importMode}" mode? This will modify your database.`)) return;
    setImporting(true);
    try {
      const fileData = await readFileAsBase64(importFile);
      const res = await API.post('/backup/import', {
        file_data: fileData, password: importPassword, mode: importMode,
      });
      toast.success(`Import complete: ${res.data.total_inserted} records inserted, ${res.data.total_skipped} skipped`);
      setShowImport(false); setImportFile(null); setImportPassword(''); setDryRunResult(null);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const handleDownloadInstructions = async () => {
    try {
      const res = await API.get('/backup/decode-instructions', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'YASH_GOLD_DECODE_INSTRUCTIONS.txt'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error('Failed to download instructions'); }
  };

  const lastExport = backupStatus?.last_export;
  const year = backupStatus?.current_year || new Date().getFullYear();

  return (
    <>
      <Card data-testid="backup-card" className="border-amber-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield size={16} className="text-amber-400" /> Data Safety Backup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {lastExport && (
            <div className="text-xs text-muted-foreground space-y-0.5">
              <p>Last export: <span className="text-foreground">{formatDateTime(lastExport.exported_at)}</span></p>
              <p>By: {lastExport.exported_by}</p>
              <p>Period: {year} cumulative</p>
            </div>
          )}
          {!lastExport && <p className="text-xs text-muted-foreground">No backups exported yet</p>}
          <div className="flex flex-col gap-2">
            <Button size="sm" className="w-full justify-start gap-2 text-xs" onClick={() => setShowExport(true)} data-testid="backup-export-btn">
              <Download size={14} /> Export Secure Backup (.dat)
            </Button>
            <Button size="sm" variant="outline" className="w-full justify-start gap-2 text-xs" onClick={() => { setShowImport(true); setDryRunResult(null); }} data-testid="backup-import-btn">
              <Upload size={14} /> Import Secure Backup (.dat)
            </Button>
            <Button size="sm" variant="ghost" className="w-full justify-start gap-2 text-xs" onClick={handleDownloadInstructions} data-testid="backup-decode-btn">
              <FileText size={14} /> Download Decode Instructions (.txt)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <Dialog open={showExport} onOpenChange={v => { if (!v) { setShowExport(false); setPassword(''); setConfirmPassword(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Shield size={18} /> Export Encrypted Backup</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs">
              <p className="font-semibold text-amber-400 mb-1">Yearly Cumulative Backup</p>
              <p className="text-muted-foreground">Period: Jan 1, {year} to now (IST)</p>
              <p className="text-muted-foreground">Includes all transactions + master data snapshot</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Encryption Password</label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Min 4 characters" data-testid="backup-password-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Confirm Password</label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password" data-testid="backup-confirm-password-input" />
            </div>
            <div className="flex items-center gap-1 text-xs text-amber-400">
              <AlertTriangle size={12} /> Keep password safe - cannot be recovered!
            </div>
            <Button className="w-full" onClick={handleExport} disabled={exporting} data-testid="backup-export-confirm-btn">
              {exporting ? <><Loader2 size={14} className="animate-spin mr-2" />Generating...</> : <><Download size={14} className="mr-2" />Export & Download</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={v => { if (!v) { setShowImport(false); setImportFile(null); setImportPassword(''); setDryRunResult(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload size={18} /> Import Backup</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Backup File (.dat)</label>
              <Input type="file" accept=".dat" ref={fileRef} onChange={handleFileSelect} data-testid="backup-import-file" />
              {importFile && <p className="text-xs text-muted-foreground mt-1">{importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)</p>}
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Password</label>
              <Input type="password" value={importPassword} onChange={e => setImportPassword(e.target.value)}
                placeholder="Enter backup password" data-testid="backup-import-password" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Import Mode</label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">Merge (skip existing records)</SelectItem>
                  <SelectItem value="replace_current_year_data">Replace current year data</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={handleDryRun} disabled={importing} data-testid="backup-dryrun-btn">
                {importing ? <Loader2 size={14} className="animate-spin" /> : 'Validate & Preview'}
              </Button>
              <Button className="flex-1" onClick={handleImport} disabled={importing || !dryRunResult} data-testid="backup-import-confirm-btn">
                {importing ? <Loader2 size={14} className="animate-spin" /> : 'Apply Import'}
              </Button>
            </div>
            {dryRunResult && (
              <div className="p-3 rounded-lg border bg-muted/30 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-400">
                  <CheckCircle size={14} /> Validation Passed
                </div>
                <p className="text-xs text-muted-foreground">
                  Period: {dryRunResult.manifest?.period_start_ist?.slice(0, 10)} to {dryRunResult.manifest?.period_end_ist?.slice(0, 10)}
                </p>
                <div className="text-xs space-y-0.5">
                  {Object.entries(dryRunResult.collections || {}).map(([col, info]) => (
                    <div key={col} className="flex justify-between">
                      <span className="text-muted-foreground">{col}</span>
                      <span className="font-mono">{info.record_count} records</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
